import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { ProposalBuilder } from '../components/ProposalBuilder';
import { useTheme } from '../contexts/ThemeContext';
import { ArrowLeft, Loader2, Settings, FileText, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TemplateBuilder() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const isNew = !templateId || templateId === 'new';

  useEffect(() => {
    fetchProducts();
    if (!isNew) {
      fetchTemplate();
    } else {
      setLoading(false);
      setShowSettings(true); // Show settings for new templates
    }
  }, [templateId]);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  const fetchTemplate = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/proposal-templates/${templateId}`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Template not found');
      const data = await response.json();
      setTemplate(data);
      setTemplateName(data.name || '');
      setTemplateDescription(data.description || '');
      setSelectedProducts(data.default_products?.map(p => p.product_id) || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (content) => {
    // Validate name
    if (!templateName.trim()) {
      setShowSettings(true);
      alert('Please enter a template name');
      return;
    }

    setSaving(true);
    setSaved(false);
    try {
      const method = isNew ? 'POST' : 'PATCH';
      const url = isNew
        ? `${BACKEND_URL}/api/proposal-templates`
        : `${BACKEND_URL}/api/proposal-templates/${templateId}`;

      // Build default_products from selected products
      const default_products = selectedProducts.map(productId => {
        const product = products.find(p => p.product_id === productId);
        return product ? {
          product_id: product.product_id,
          product_name: product.name,
          include_variants: product.variants?.map(v => v.variant_id) || []
        } : null;
      }).filter(Boolean);

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...template,
          builder_content: content,
          name: templateName.trim(),
          description: templateDescription.trim(),
          default_products: default_products,
          sections: template?.sections || []
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save');
      }
      
      const savedTemplate = await response.json();
      if (isNew) {
        navigate(`/proposals/builder/${savedTemplate.template_id}`, { replace: true });
      }
      setTemplate(savedTemplate);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Save failed:', err);
      alert('Failed to save template: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  if (loading) {
    return (
      <AppLayout title="Template Builder">
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-[#A2FF00]" />
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout title="Template Builder">
        <div className="flex flex-col items-center justify-center h-96">
          <p className={`text-lg mb-4 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>{error}</p>
          <button
            onClick={() => navigate('/proposals/templates')}
            className="flex items-center gap-2 px-4 py-2 bg-[#A2FF00] text-black rounded-lg"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Templates
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Template Builder" fullWidth>
      <div className="flex h-[calc(100vh-64px)]">
        {/* Left Sidebar - Template Settings */}
        <div className={`w-72 flex-shrink-0 border-r overflow-y-auto ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-gray-200 bg-gray-50'}`}>
          <div className="p-4 space-y-4">
            {/* Back Button */}
            <button
              onClick={() => navigate('/proposals/templates')}
              className={`flex items-center gap-2 text-sm ${isDark ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Templates
            </button>

            {/* Template Name */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
                Template Name *
              </label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Enter template name..."
                className={`w-full px-3 py-2 rounded-lg border ${
                  isDark 
                    ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' 
                    : 'bg-white border-gray-300 placeholder-gray-400'
                } focus:outline-none focus:ring-2 focus:ring-[#A2FF00]`}
              />
            </div>

            {/* Description */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
                Description
              </label>
              <textarea
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Template description..."
                rows={2}
                className={`w-full px-3 py-2 rounded-lg border resize-none ${
                  isDark 
                    ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' 
                    : 'bg-white border-gray-300 placeholder-gray-400'
                } focus:outline-none focus:ring-2 focus:ring-[#A2FF00]`}
              />
            </div>

            {/* Products Section */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
                Default Products
              </label>
              <p className={`text-xs mb-2 ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>
                Select products to include in proposals created from this template
              </p>
              <div className={`space-y-2 max-h-60 overflow-y-auto rounded-lg border p-2 ${isDark ? 'border-zinc-700 bg-zinc-800/50' : 'border-gray-200 bg-white'}`}>
                {products.length === 0 ? (
                  <p className={`text-sm py-2 text-center ${isDark ? 'text-zinc-500' : 'text-gray-500'}`}>
                    No products available
                  </p>
                ) : (
                  products.map((product) => (
                    <button
                      key={product.product_id}
                      onClick={() => toggleProduct(product.product_id)}
                      className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                        selectedProducts.includes(product.product_id)
                          ? 'bg-[#A2FF00]/20 border border-[#A2FF00]'
                          : isDark 
                            ? 'hover:bg-zinc-700 border border-transparent' 
                            : 'hover:bg-gray-100 border border-transparent'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                        selectedProducts.includes(product.product_id)
                          ? 'bg-[#A2FF00] border-[#A2FF00]'
                          : isDark ? 'border-zinc-600' : 'border-gray-300'
                      }`}>
                        {selectedProducts.includes(product.product_id) && (
                          <Check className="h-3 w-3 text-black" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {product.name}
                        </div>
                        <div className={`text-xs ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>
                          £{product.price_gbp} / {product.billing_frequency}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Save Status */}
            {saved && (
              <div className="flex items-center gap-2 text-sm text-green-500">
                <Check className="h-4 w-4" />
                Template saved!
              </div>
            )}
          </div>
        </div>

        {/* Main Editor Area */}
        <div className="flex-1 overflow-hidden">
          <ProposalBuilder
            initialContent={template?.builder_content}
            onSave={handleSave}
            saving={saving}
          />
        </div>
      </div>
    </AppLayout>
  );
}
